import { useState, useRef, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Upload, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  ALL_FIELDS,
  applyMappingToRows,
  bulkInsertPermits,
  detectColumnMapping,
  getDefaultPermitMapping,
  hasRequiredMozaicColumns,
  identityMappingFromMozaicHeaders,
  savePermitMapping,
  type PermitColumnMapping,
  type PermitField,
  type ParsedPermit,
} from '@/services/db/permitsImport';

interface Props {
  dsrId: string;
  tenantId: string;
  onClose: () => void;
  onImported: (count: number) => void;
}

type Stage =
  | { kind: 'pick' }
  | {
      kind: 'map';
      filename: string;
      cols: string[];
      raw: Record<string, unknown>[];
      map: Record<string, PermitField>;
    }
  | {
      kind: 'preview';
      filename: string;
      rows: ParsedPermit[];
      errors: string[];
    };

export default function PermitsImportPanel({
  dsrId,
  tenantId,
  onClose,
  onImported,
}: Props) {
  const [stage, setStage] = useState<Stage>({ kind: 'pick' });
  const [importing, setImporting] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [savedMapping, setSavedMapping] = useState<PermitColumnMapping | null>(null);
  const [saveMappingChecked, setSaveMappingChecked] = useState(false);
  const [mappingName, setMappingName] = useState('');
  const [savedSuggestionDismissed, setSavedSuggestionDismissed] = useState(false);
  const [lastUsedMap, setLastUsedMap] = useState<Record<string, PermitField> | null>(null);

  useEffect(() => {
    let cancelled = false;
    getDefaultPermitMapping().then((m) => {
      if (!cancelled) setSavedMapping(m);
    });
    return () => { cancelled = true; };
  }, []);

  const useSavedMapping = () => {
    if (stage.kind !== 'map' || !savedMapping) return;
    const next: Record<string, PermitField> = {};
    for (const [col, field] of Object.entries(savedMapping.column_map)) {
      if (stage.cols.includes(col)) next[col] = field;
    }
    setStage({ ...stage, map: next });
    setSavedSuggestionDismissed(true);
  };

  const handleFile = (file: File) => {
    setTopError(null);
    const reader = new FileReader();
    const isXlsx = file.name.toLowerCase().endsWith('.xlsx');
    reader.onload = (ev) => {
      const result = ev.target?.result;
      if (!result) return;
      let raw: Record<string, unknown>[] = [];
      try {
        if (isXlsx) {
          const wb = XLSX.read(result, { type: 'binary' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          raw = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });
        } else {
          const parsed = Papa.parse<Record<string, unknown>>(result as string, {
            header: true,
            skipEmptyLines: true,
          });
          raw = parsed.data;
        }
      } catch (e) {
        setTopError('Failed to parse: ' + (e instanceof Error ? e.message : ''));
        return;
      }
      if (raw.length === 0) {
        setTopError('No rows found in file.');
        return;
      }
      const cols = Object.keys(raw[0]);
      if (hasRequiredMozaicColumns(cols)) {
        const id = identityMappingFromMozaicHeaders(cols);
        const { rows, errors } = applyMappingToRows(raw, id);
        setLastUsedMap(id);
        setStage({ kind: 'preview', filename: file.name, rows, errors });
        return;
      }
      const auto = detectColumnMapping(cols);
      setStage({
        kind: 'map',
        filename: file.name,
        cols,
        raw,
        map: auto,
      });
      setSavedSuggestionDismissed(false);
    };
    if (isXlsx) reader.readAsBinaryString(file);
    else reader.readAsText(file);
  };

  const confirmMapping = () => {
    if (stage.kind !== 'map') return;
    const mapped = new Set(Object.values(stage.map));
    for (const required of ['permit_type', 'permit_number', 'issued_by', 'issue_datetime'] as PermitField[]) {
      if (!mapped.has(required)) {
        const label = ALL_FIELDS.find((f) => f.key === required)?.label ?? required;
        setTopError(`Please map a column to "${label}" before continuing.`);
        return;
      }
    }
    const { rows, errors } = applyMappingToRows(stage.raw, stage.map);
    setLastUsedMap(stage.map);
    setStage({ kind: 'preview', filename: stage.filename, rows, errors });
  };

  const doImport = async () => {
    if (stage.kind !== 'preview') return;
    setImporting(true);
    setTopError(null);
    const result = await bulkInsertPermits(dsrId, stage.rows, tenantId);
    if (result.errors.length > 0) {
      setImporting(false);
      setTopError(result.errors.join('; '));
      return;
    }
    if (saveMappingChecked && lastUsedMap) {
      try {
        await savePermitMapping({
          name: mappingName.trim() || 'Default',
          columnMap: lastUsedMap,
          makeDefault: true,
        });
      } catch (e) {
        console.warn('Could not save column mapping:', e);
      }
    }
    setImporting(false);
    onImported(result.inserted);
  };

  return (
    <Card className="border-mozaic-teal/40">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
        <h3 className="text-sm font-semibold">Import Permits</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {topError && (
          <div className="rounded border border-mozaic-red/40 bg-mozaic-red/10 px-3 py-2 text-xs text-mozaic-red">
            {topError}
          </div>
        )}

        {stage.kind === 'pick' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Upload a CSV or Excel (.xlsx) file. The MOZAIC template imports
              automatically; other files open a column mapper.{' '}
              <a
                href="/templates/mozaic-permits-template.csv"
                download
                className="text-mozaic-teal underline"
              >
                Download template
              </a>
              .
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
            <Button
              onClick={() => fileRef.current?.click()}
              className="bg-mozaic-teal hover:bg-mozaic-teal/90"
            >
              <Upload className="mr-1 h-4 w-4" /> Choose File
            </Button>
          </div>
        )}

        {stage.kind === 'map' && (
          <div className="space-y-3">
            {savedMapping && !savedSuggestionDismissed && (
              <div className="flex items-center justify-between gap-3 rounded border border-mozaic-teal/40 bg-mozaic-teal/10 px-3 py-2 text-xs">
                <span>
                  Saved mapping available: <strong>{savedMapping.mapping_name}</strong>
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => setSavedSuggestionDismissed(true)}
                  >
                    Map manually
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 bg-mozaic-teal text-xs hover:bg-mozaic-teal/90"
                    onClick={useSavedMapping}
                  >
                    Use saved
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Match each file column to a MOZAIC field. Permit type, number, issued by, and
              issue date/time are required.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {stage.cols.map((col) => (
                <div key={col} className="space-y-1">
                  <Label className="text-xs">{col}</Label>
                  <Select
                    value={stage.map[col] ?? '__none__'}
                    onValueChange={(v) => {
                      const next = { ...stage.map };
                      if ((v as string) === '__none__') delete next[col];
                      else next[col] = v as PermitField;
                      setStage({ ...stage, map: next });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— ignore —</SelectItem>
                      {ALL_FIELDS.map((f) => (
                        <SelectItem key={f.key} value={f.key}>
                          {f.label}
                          {f.required ? ' *' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStage({ kind: 'pick' })}>
                Back
              </Button>
              <Button
                onClick={confirmMapping}
                className="bg-mozaic-teal hover:bg-mozaic-teal/90"
              >
                Preview
              </Button>
            </div>
          </div>
        )}

        {stage.kind === 'preview' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {stage.rows.length} valid row{stage.rows.length === 1 ? '' : 's'}{' '}
              {stage.errors.length > 0
                ? `· ${stage.errors.length} skipped`
                : ''}{' '}
              from <span className="font-medium">{stage.filename}</span>.
            </p>
            {stage.rows.length > 0 && (
              <div className="overflow-x-auto rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Issued</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stage.rows.slice(0, 5).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="capitalize">{r.permit_type.replace(/_/g, ' ')}</TableCell>
                        <TableCell>{r.permit_number}</TableCell>
                        <TableCell className="capitalize">{r.status}</TableCell>
                        <TableCell>{new Date(r.issued_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {stage.errors.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-mozaic-orange">
                  {stage.errors.length} row{stage.errors.length === 1 ? '' : 's'} skipped
                </summary>
                <ul className="mt-1 max-h-32 list-disc space-y-0.5 overflow-auto pl-5 text-muted-foreground">
                  {stage.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </details>
            )}
            {lastUsedMap && (
              <div className="rounded border border-border bg-muted/30 px-3 py-2 text-xs">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-input text-mozaic-teal accent-mozaic-teal"
                    checked={saveMappingChecked}
                    onChange={(e) => setSaveMappingChecked(e.target.checked)}
                  />
                  <span>Save this mapping as the tenant default for future imports</span>
                </label>
                {saveMappingChecked && (
                  <Input
                    className="mt-2 h-8 text-xs"
                    placeholder="Mapping name (e.g. Operator X export)"
                    value={mappingName}
                    onChange={(e) => setMappingName(e.target.value)}
                  />
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setStage({ kind: 'pick' })}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button
                onClick={doImport}
                disabled={importing || stage.rows.length === 0}
                className="bg-mozaic-teal hover:bg-mozaic-teal/90"
              >
                {importing
                  ? 'Importing…'
                  : `Import ${stage.rows.length} ${stage.rows.length === 1 ? 'permit' : 'permits'}`}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
